import Client from 'shopify-buy';

// Define the Shopify client type locally
interface ShopifyClient {
  product: {
    fetch(id: string): Promise<any>;
    fetchByHandle(handle: string): Promise<any>;
  };
  checkout: {
    create(): Promise<any>;
    fetch(id: string): Promise<any>;
    addLineItems(checkoutId: string, lineItems: any[]): Promise<any>;
    updateLineItems(checkoutId: string, lineItems: any[]): Promise<any>;
    removeLineItems(checkoutId: string, lineItemIds: string[]): Promise<any>;
  };
}

// Types for our cart and products
export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  handle: string;
  images: ShopifyImage[];
  variants: ShopifyVariant[];
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  };
  image?: ShopifyImage;
  availableForSale: boolean;
  quantityAvailable: number;
}

export interface ShopifyImage {
  id: string;
  url: string;
  altText?: string;
  width: number;
  height: number;
}

export interface CartItem {
  id: string;
  quantity: number;
  variant: ShopifyVariant;
  product: ShopifyProduct;
}

export interface Cart {
  id: string;
  lineItems: CartItem[];
  subtotalPrice: {
    amount: string;
    currencyCode: string;
  };
  totalPrice: {
    amount: string;
    currencyCode: string;
  };
  webUrl: string;
  checkoutUrl: string;
}

class ShopifyStorefrontService {
  private client: ShopifyClient;
  private cart: Cart | null = null;
  private cartListeners: Array<(cart: Cart | null) => void> = [];

  constructor() {
    // Initialize with your Shopify domain and storefront token
    this.client = Client.buildClient({
      domain: '3d0ktv-1n.myshopify.com',
      storefrontAccessToken: 'd36dcf07dcaa72006351b1d2200af5d7', // Your HAVEN Website Integration token
    });

    // Load existing cart from localStorage
    this.loadCartFromStorage();
  }



  // Product methods
  async getProduct(productId: string): Promise<ShopifyProduct | null> {
    try {
      let product = null;
      
      // Try different ID formats
      // First try with the provided ID
      try {
        product = await this.client.product.fetch(productId);
      } catch (firstError) {
        // If it's a numeric ID, try with gid format
        if (/^\d+$/.test(productId)) {
          try {
            const gidFormat = `gid://shopify/Product/${productId}`;
            product = await this.client.product.fetch(gidFormat);
          } catch (secondError) {
            // If it's a gid format, try with just the numeric part
            if (productId.includes('gid://shopify/Product/')) {
              const numericId = productId.split('/').pop();
              if (numericId) {
                try {
                  product = await this.client.product.fetch(numericId);
                } catch (thirdError) {
                  throw thirdError;
                }
              }
            } else {
              throw secondError;
            }
          }
        } else {
          throw firstError;
        }
      }
      
      if (product) {
        return this.formatProduct(product);
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  async getProductByHandle(handle: string): Promise<ShopifyProduct | null> {
    try {
      const product = await this.client.product.fetchByHandle(handle);
      return this.formatProduct(product);
    } catch (error) {
      console.error('Error fetching product by handle:', error);
      return null;
    }
  }





  // Cart methods
  async createCart(): Promise<Cart> {
    try {
      const checkout = await this.client.checkout.create();
      this.cart = this.formatCart(checkout);
      this.saveCartToStorage();
      this.notifyCartListeners();
      return this.cart;
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  }

  async addToCart(variantId: string, quantity: number = 1): Promise<Cart> {
    try {
      // Create cart if it doesn't exist
      if (!this.cart) {
        await this.createCart();
      }

      if (!this.cart) {
        throw new Error('Failed to create cart');
      }

      const lineItemsToAdd = [{
        variantId,
        quantity,
      }];

      const updatedCheckout = await this.client.checkout.addLineItems(this.cart.id, lineItemsToAdd);
      
      this.cart = this.formatCart(updatedCheckout);
      
      this.saveCartToStorage();
      this.notifyCartListeners();
      return this.cart;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  async updateCartItem(lineItemId: string, quantity: number): Promise<Cart> {
    try {
      if (!this.cart) {
        throw new Error('No cart found');
      }

      const lineItemsToUpdate = [{
        id: lineItemId,
        quantity,
      }];

      const updatedCheckout = await this.client.checkout.updateLineItems(this.cart.id, lineItemsToUpdate);
      this.cart = this.formatCart(updatedCheckout);
      this.saveCartToStorage();
      this.notifyCartListeners();
      return this.cart;
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  }

  async removeFromCart(lineItemId: string): Promise<Cart> {
    try {
      if (!this.cart) {
        throw new Error('No cart found');
      }

      const updatedCheckout = await this.client.checkout.removeLineItems(this.cart.id, [lineItemId]);
      this.cart = this.formatCart(updatedCheckout);
      this.saveCartToStorage();
      this.notifyCartListeners();
      return this.cart;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  getCart(): Cart | null {
    return this.cart;
  }

  async clearCart(): Promise<void> {
    this.cart = null;
    localStorage.removeItem('shopify_cart');
    this.notifyCartListeners();
  }

  // Method to clear cart and localStorage for debugging
  async clearCartAndStorage(): Promise<void> {
    this.cart = null;
    localStorage.removeItem('shopify_cart');
    this.notifyCartListeners();
  }

  // Cart listeners for real-time updates
  onCartUpdate(callback: (cart: Cart | null) => void): () => void {
    this.cartListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.cartListeners.indexOf(callback);
      if (index > -1) {
        this.cartListeners.splice(index, 1);
      }
    };
  }

  private notifyCartListeners(): void {
    this.cartListeners.forEach(callback => callback(this.cart));
  }

  // Helper method to get better product titles from cart items
  private getProductTitleFromVariant(item: any): string {
    
    // Try multiple sources for product title in order of preference
    if (item.variant?.product?.title) {
      return item.variant.product.title;
    }
    if (item.title) {
      return item.title;
    }
    if (item.variant?.title && item.variant.title !== 'Default Title') {
      return item.variant.title;
    }
    
    // Map known variant IDs to product names as fallback
    const variantId = item.variant?.id || '';
    
    // Updated with correct variant IDs from the console logs
    if (variantId.includes('47827872809099') || variantId.includes('7827872809099')) {
      return 'HAVEN Room Monitor';
    }
    if (variantId.includes('47847604977803') || variantId.includes('7847604977803')) {
      return 'HAVEN Controller Bundle';
    }
    if (variantId.includes('43622638059659') || variantId.includes('7847606157451')) {
      return 'Professionally Installed HAVEN Central Air Bundle';
    }
    
    // Generic fallback
    return 'HAVEN Product';
  }

  // Utility methods
  private formatProduct(product: any): ShopifyProduct {
    
    // Defensive programming - ensure arrays exist
    const images = Array.isArray(product.images) ? product.images : [];
    const variants = Array.isArray(product.variants) ? product.variants : [];
    
    const formattedProduct = {
      id: product.id,
      title: product.title || 'Unknown Product',
      description: product.description || '',
      handle: product.handle || '',
      images: images.map((img: any) => ({
        id: img.id,
        url: img.src || img.url,
        altText: img.altText || '',
        width: img.width || 0,
        height: img.height || 0,
      })),
      variants: variants.map((variant: any) => ({
        id: variant.id,
        title: variant.title || 'Default',
        price: {
          amount: variant.price?.amount || '0',
          currencyCode: variant.price?.currencyCode || 'USD',
        },
        compareAtPrice: variant.compareAtPrice ? {
          amount: variant.compareAtPrice.amount,
          currencyCode: variant.compareAtPrice.currencyCode,
        } : undefined,
        image: variant.image ? {
          id: variant.image.id,
          url: variant.image.src || variant.image.url,
          altText: variant.image.altText || '',
          width: variant.image.width || 0,
          height: variant.image.height || 0,
        } : undefined,
        availableForSale: variant.available !== false,
        quantityAvailable: variant.quantityAvailable || 0,
      })),
      priceRange: {
        minVariantPrice: {
          amount: variants[0]?.price?.amount || '0',
          currencyCode: variants[0]?.price?.currencyCode || 'USD',
        },
      },
    };
    
    return formattedProduct;
  }

  private formatCart(checkout: any): Cart {
    return {
      id: checkout.id,
      lineItems: (checkout.lineItems || []).map((item: any) => {
        return {
          id: item.id,
          quantity: item.quantity || 1,
          variant: {
            id: item.variant?.id || '',
            title: item.variant?.title || 'Unknown Variant',
            price: {
              amount: item.variant?.price?.amount || '0',
              currencyCode: item.variant?.price?.currencyCode || 'USD',
            },
            compareAtPrice: item.variant?.compareAtPrice ? {
              amount: item.variant.compareAtPrice.amount,
              currencyCode: item.variant.compareAtPrice.currencyCode,
            } : undefined,
            image: item.variant?.image ? {
              id: item.variant.image.id,
              url: item.variant.image.src || item.variant.image.url,
              altText: item.variant.image.altText || '',
              width: item.variant.image.width || 0,
              height: item.variant.image.height || 0,
            } : undefined,
            availableForSale: item.variant?.available !== false,
            quantityAvailable: item.variant?.quantityAvailable || 0,
          },
          product: item.variant?.product ? (() => {
            const productData = item.variant.product;
            
            // Check if we have a complete product object with title
            if (productData.title) {
              return this.formatProduct(productData);
            } else {
              // Product object is incomplete, create a basic one using cart item data
              return {
                id: productData.id || item.variant?.id || '',
                title: item.title || 'HAVEN Product',
                description: productData.description || '',
                handle: productData.handle || '',
                images: item.variant?.image ? [item.variant.image] : [],
                variants: item.variant ? [item.variant] : [],
                priceRange: {
                  minVariantPrice: {
                    amount: item.variant?.price?.amount || '0',
                    currencyCode: item.variant?.price?.currencyCode || 'USD',
                  },
                },
              };
            }
          })() : (() => {
            const productTitle = this.getProductTitleFromVariant(item);
            return {
              id: item.variant?.id || '',
              title: productTitle,
              description: item.variant?.product?.description || '',
              handle: item.variant?.product?.handle || '',
              images: item.variant?.image ? [item.variant.image] : [],
              variants: item.variant ? [item.variant] : [],
              priceRange: {
                minVariantPrice: {
                  amount: item.variant?.price?.amount || '0',
                  currencyCode: item.variant?.price?.currencyCode || 'USD',
                },
              },
            };
          })(),
        };
      }),
      subtotalPrice: {
        amount: checkout.subtotalPrice?.amount || '0',
        currencyCode: checkout.subtotalPrice?.currencyCode || 'USD',
      },
      totalPrice: {
        amount: checkout.totalPrice?.amount || '0',
        currencyCode: checkout.totalPrice?.currencyCode || 'USD',
      },
      webUrl: checkout.webUrl || '',
      checkoutUrl: checkout.webUrl || '',
    };
  }

  private saveCartToStorage(): void {
    if (this.cart) {
      localStorage.setItem('shopify_cart', JSON.stringify({
        id: this.cart.id,
        timestamp: Date.now(),
      }));
    }
  }

  private async loadCartFromStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem('shopify_cart');
      if (stored) {
        const { id, timestamp } = JSON.parse(stored);
        
        // Only load if cart is less than 24 hours old
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          const checkout = await this.client.checkout.fetch(id);
          if (checkout && checkout.lineItems.length > 0) {
            this.cart = this.formatCart(checkout);
            this.notifyCartListeners();
          }
        } else {
          localStorage.removeItem('shopify_cart');
        }
      }
    } catch (error) {
      console.error('Error loading cart from storage:', error);
      localStorage.removeItem('shopify_cart');
    }
  }

  // Get cart item count
  getCartItemCount(): number {
    if (!this.cart) return 0;
    return this.cart.lineItems.reduce((total, item) => total + item.quantity, 0);
  }

  // Get cart total
  getCartTotal(): string {
    if (!this.cart) return '0.00';
    return this.cart.totalPrice.amount;
  }
}

// Export singleton instance
export const shopifyStorefront = new ShopifyStorefrontService();
export default shopifyStorefront; 